import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { MapPin, Navigation } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface JobDistanceMapProps {
  jobLocation: string;
  jobTitle: string;
}

interface Coordinates {
  lat: number;
  lng: number;
}

const stateCoordinates: Record<string, Coordinates> = {
  'alabama': { lat: 32.806671, lng: -86.791130 },
  'alaska': { lat: 61.370716, lng: -152.404419 },
  'arizona': { lat: 33.729759, lng: -111.431221 },
  'arkansas': { lat: 34.969704, lng: -92.373123 },
  'california': { lat: 36.116203, lng: -119.681564 },
  'colorado': { lat: 39.059811, lng: -105.311104 },
  'connecticut': { lat: 41.597782, lng: -72.755371 },
  'delaware': { lat: 39.318523, lng: -75.507141 },
  'florida': { lat: 27.766279, lng: -81.686783 },
  'georgia': { lat: 33.040619, lng: -83.643074 },
  'hawaii': { lat: 21.094318, lng: -157.498337 },
  'idaho': { lat: 44.240459, lng: -114.478828 },
  'illinois': { lat: 40.349457, lng: -88.986137 },
  'indiana': { lat: 39.849426, lng: -86.258278 },
  'iowa': { lat: 42.011539, lng: -93.210526 },
  'kansas': { lat: 38.526600, lng: -96.726486 },
  'kentucky': { lat: 37.668140, lng: -84.670067 },
  'louisiana': { lat: 31.169546, lng: -91.867805 },
  'maine': { lat: 44.693947, lng: -69.381927 },
  'maryland': { lat: 39.063946, lng: -76.802101 },
  'massachusetts': { lat: 42.230171, lng: -71.530106 },
  'michigan': { lat: 43.326618, lng: -84.536095 },
  'minnesota': { lat: 45.694454, lng: -93.900192 },
  'mississippi': { lat: 32.741646, lng: -89.678696 },
  'missouri': { lat: 38.456085, lng: -92.288368 },
  'montana': { lat: 46.921925, lng: -110.454353 },
  'nebraska': { lat: 41.125370, lng: -98.268082 },
  'nevada': { lat: 38.313515, lng: -117.055374 },
  'new hampshire': { lat: 43.452492, lng: -71.563896 },
  'new jersey': { lat: 40.298904, lng: -74.521011 },
  'new mexico': { lat: 34.840515, lng: -106.248482 },
  'new york': { lat: 42.165726, lng: -74.948051 },
  'north carolina': { lat: 35.630066, lng: -79.806419 },
  'north dakota': { lat: 47.528912, lng: -99.784012 },
  'ohio': { lat: 40.388783, lng: -82.764915 },
  'oklahoma': { lat: 35.565342, lng: -96.928917 },
  'oregon': { lat: 44.572021, lng: -122.070938 },
  'pennsylvania': { lat: 40.590752, lng: -77.209755 },
  'rhode island': { lat: 41.680893, lng: -71.511780 },
  'south carolina': { lat: 33.856892, lng: -80.945007 },
  'south dakota': { lat: 44.299782, lng: -99.438828 },
  'tennessee': { lat: 35.747845, lng: -86.692345 },
  'texas': { lat: 31.054487, lng: -97.563461 },
  'utah': { lat: 40.150032, lng: -111.862434 },
  'vermont': { lat: 44.045876, lng: -72.710686 },
  'virginia': { lat: 37.769337, lng: -78.169968 },
  'washington': { lat: 47.400902, lng: -121.490494 },
  'west virginia': { lat: 38.491226, lng: -80.954453 },
  'wisconsin': { lat: 44.268543, lng: -89.616508 },
  'wyoming': { lat: 42.755966, lng: -107.302490 }
};

function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function parseLocationToCoordinates(location: string): Coordinates | null {
  const locationLower = location.toLowerCase();

  for (const [state, coords] of Object.entries(stateCoordinates)) {
    if (locationLower.includes(state)) {
      return coords;
    }
  }

  return null;
}

export default function JobDistanceMap({ jobLocation, jobTitle }: JobDistanceMapProps) {
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null);
  const [jobCoords, setJobCoords] = useState<Coordinates | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const coords = parseLocationToCoordinates(jobLocation);
    setJobCoords(coords);
  }, [jobLocation]);

  const getUserLocation = () => {
    setLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(coords);
        setLoading(false);
        setShowMap(true);

        if (jobCoords) {
          const dist = calculateDistance(
            coords.lat,
            coords.lng,
            jobCoords.lat,
            jobCoords.lng
          );
          setDistance(dist);
        }
      },
      (err) => {
        setError('Unable to get your location. Please enable location services.');
        setLoading(false);
      }
    );
  };

  if (!jobCoords) {
    return null;
  }

  return (
    <div className="border-t border-gray-200 pt-6 mt-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <MapPin className="h-5 w-5 text-green-600" />
        Location & Distance
      </h3>

      {!showMap ? (
        <button
          onClick={getUserLocation}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Navigation className="h-4 w-4" />
          {loading ? 'Getting your location...' : 'Show Distance from My Location'}
        </button>
      ) : (
        <div className="space-y-4">
          {distance && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 font-medium">
                Approximate distance: <span className="text-xl">{Math.round(distance)}</span> miles
              </p>
              <p className="text-sm text-green-600 mt-1">
                Estimated driving time: {Math.round(distance / 60)} hours
              </p>
            </div>
          )}

          <div className="h-96 rounded-lg overflow-hidden border border-gray-300">
            <MapContainer
              center={jobCoords}
              zoom={6}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {jobCoords && (
                <Marker position={[jobCoords.lat, jobCoords.lng]}>
                  <Popup>
                    <strong>{jobTitle}</strong><br />
                    {jobLocation}
                  </Popup>
                </Marker>
              )}

              {userLocation && (
                <>
                  <Marker position={[userLocation.lat, userLocation.lng]}>
                    <Popup>Your Location</Popup>
                  </Marker>
                  <Polyline
                    positions={[
                      [userLocation.lat, userLocation.lng],
                      [jobCoords.lat, jobCoords.lng]
                    ]}
                    color="green"
                    weight={3}
                    dashArray="10, 10"
                  />
                </>
              )}
            </MapContainer>
          </div>

          <button
            onClick={() => setShowMap(false)}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Hide Map
          </button>
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
    </div>
  );
}
